import type { WorkspaceAreaKind } from '../../folder-api';

declare const viewBackedAppKindBrand: unique symbol;

export type ViewBackedAppKind<TKind extends string = string> = TKind & {
  readonly [viewBackedAppKindBrand]: 'ViewBackedAppKind';
};

export type ViewBackedAppSourceLifecycleState =
  | 'active'
  | 'deleted'
  | 'trashed';

export type ViewBackedAppSourceScope = 'private' | 'shared';

export type ViewBackedAppSourceSyncState =
  | 'failed'
  | 'local'
  | 'pending'
  | 'synced';

export interface ViewBackedAppAreaRef {
  kind: WorkspaceAreaKind;
  name: string;
  ownerUserId: string | null;
  rootFolderId: string;
  systemFolderId: string | null;
  workspaceId: string;
}

export interface ViewBackedAppDescriptor<
  TDiagnostics = unknown,
  TKind extends string = string,
> {
  appKind: ViewBackedAppKind<TKind>;
  apiSegment: string;
  defaultTitle: string;
  defaultViewId: string;
  maxVisibleSources?: number;
  readDiagnostics(input: { view: unknown }): TDiagnostics | null;
  sourceMutationOperations?: readonly ViewBackedAppSourceMutationName[];
  resourcePrefix: `${string}:`;
  viewMode: string;
}

export type ViewBackedAppDescriptorInit<
  TDiagnostics = unknown,
  TKind extends string = string,
> = Omit<ViewBackedAppDescriptor<TDiagnostics, TKind>, 'appKind'> & {
  appKind: TKind;
};

export function defineViewBackedAppDescriptor<
  const TKind extends string,
  TDiagnostics = unknown,
>(
  descriptor: ViewBackedAppDescriptorInit<TDiagnostics, TKind>
): ViewBackedAppDescriptor<TDiagnostics, TKind> {
  return descriptor as unknown as ViewBackedAppDescriptor<TDiagnostics, TKind>;
}

export type ViewBackedAppSourceMutationName =
  | 'attachSource'
  | 'createSource'
  | 'updateSourcePreference';

export interface ViewBackedAppSource {
  accessDocId: string;
  appKind: ViewBackedAppKind;
  area: ViewBackedAppAreaRef | null;
  blockId: string;
  canEdit: boolean;
  canManage: boolean;
  color: string | null;
  databaseId: string;
  deletedAt: string | null;
  docId: string;
  id: string;
  lifecycleState: ViewBackedAppSourceLifecycleState;
  projectionError?: string | null;
  scope: ViewBackedAppSourceScope;
  syncState?: ViewBackedAppSourceSyncState;
  title: string;
  trashedAt: string | null;
  viewId: string;
  viewMode: string;
  visible: boolean;
}

export interface ViewBackedAppSourceCandidate {
  appKind: ViewBackedAppKind;
  area: ViewBackedAppAreaRef | null;
  blockId: string;
  databaseId: string;
  docId: string;
  documentTitle: string | null;
  projectionError?: string | null;
  title: string;
  viewId: string;
  viewMode: string;
}

export interface ViewBackedAppSourceAttachInput {
  areaRootFolderId?: string | null;
  blockId: string;
  databaseId: string;
  docId: string;
  title?: string | null;
  viewId: string;
}

export interface ViewBackedAppSourcePreferencePatch {
  colorOverride?: string | null;
  titleOverride?: string | null;
  visible?: boolean;
}

export interface ViewBackedAppSourceRef {
  appKind: ViewBackedAppKind;
  blockId: string;
  databaseId: string;
  docId: string;
  viewId: string;
  viewMode: string;
}

export function viewBackedAppSourceRefKey(
  source: Pick<
    ViewBackedAppSourceRef,
    'blockId' | 'databaseId' | 'docId' | 'viewId'
  >
): string {
  return JSON.stringify([
    source.docId,
    source.blockId,
    source.databaseId,
    source.viewId,
  ]);
}
